import { DictItem } from "@/types/dict";
import dictApi, { dictListIgnoreEnum } from "@/apis/dict";
import { brandModelType } from "./utils";
import { App, Button, Divider, Input, notification, Select, SelectProps } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type SelectBrandModelProps<T> = Omit<SelectProps<T>, "options"> & { productType: string; brandName: string };

function SelectBrandModel<T>(props: SelectBrandModelProps<T>) {
  const { productType, brandName, ...selctProps } = props;
  const { t } = useTranslation();
  const [brandModel, setBrandModel] = useState<DictItem[]>([]);
  const [isShowAdd, setIsShowAdd] = useState<boolean>(false);
  const [addVal, setAddVal] = useState<string>("");
  const [adding, setAdding] = useState<boolean>(false);
  const [openDrop, setOpenDrop] = useState<boolean>(false);

  // 型号字典 type 为 `${产品类型}-${品牌名称}` 组合键，同时关联产品类型与品牌名称
  const modelType = brandModelType(productType, brandName);

  // 按产品类型 + 品牌名称拉取品牌型号字典
  const fetchList = async () => {
    if (!productType || !brandName) return;
    try {
      const list = await dictListIgnoreEnum(modelType);
      setBrandModel(list);
    } catch { }
  };

  useEffect(() => {
    if (openDrop) fetchList();
  }, [openDrop, modelType]);

  const closeAdd = () => {
    setIsShowAdd(false);
    setAddVal("");
  };

  const handleAdd = () => {
    const desc = addVal.trim();
    if (!desc || brandModel.some(item => item.desc === desc)) {
      closeAdd();
      return;
    }
    setAdding(true);
    dictApi.addIgnoreEnum(modelType, { key: desc, desc })
      .then((res: DictItem) => {
        setBrandModel([...brandModel, res]);
        closeAdd();
      })
      .catch((err) => {
        notification.error({
          placement: 'top',
          message: t("common.addError"),
          description: err === 10010 ? t("common.networkError") : t("common.serverError"),
        });
      })
      .finally(() => setAdding(false));
  };

  return (
    <Select
      {...selctProps}
      showSearch
      open={openDrop}
      onDropdownVisibleChange={(open) => {
        setOpenDrop(open);
        if (!open) closeAdd();
        props.onDropdownVisibleChange?.(open);
      }}
      options={
        brandModel.map(item => ({ key: item.id, label: item.desc, value: item.desc }))
      }
      filterOption={(input, option) =>
        String(option?.label ?? "").toLowerCase().includes(String(input).toLowerCase())
      }
      placeholder={t('purchase.brandModel')}
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: "8px 0" }} />
          {isShowAdd ? (
            <div style={{ display: "flex", gap: 4, padding: "0 4px" }}>
              <Input
                autoFocus
                size="small"
                value={addVal}
                onChange={(e) => setAddVal(e.target.value)}
                onPressEnter={handleAdd}
                placeholder={t('purchase.brandModel')}
                maxLength={100}
              />
              <Button type="link" size="small" icon={<CheckOutlined />} loading={adding} onClick={handleAdd} />
              <Button type="link" size="small" danger icon={<CloseOutlined />} onClick={closeAdd} />
            </div>
          ) : (
            <Button
              type="text"
              size="small"
              style={{ width: "100%", color: "#1677ff" }}
              icon={<PlusOutlined />}
              onClick={() => setIsShowAdd(true)}
            >
              {t("common.add")}
            </Button>
          )}
        </>
      )}
    />
  )
};

export default SelectBrandModel;
